import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInrichtingHistorie, getInrichtingDetail, getGebieden } from '../api/api';
import { fmtId, fmtDatumNL, kenmerkTypeLabel, doelLabel } from '../util/format';
import Modal from '../components/Modal';
import KaartMetLegenda from '../components/KaartMetLegenda';

function Veld({ label, children }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className="field-value">{children}</span>
    </div>
  );
}

export default function InrichtingHistorieModal() {
  const { id, periode_id } = useParams();
  const navigate = useNavigate();

  const [slices, setSlices] = useState([]);
  const [houder, setHouder] = useState(null);
  const [gebieden, setGebieden] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);

  const sluit = () => navigate(`/beregening/inrichtingen/${id}`);

  useEffect(() => {
    let actief = true;
    (async () => {
      setLaden(true);
      try {
        const [hist, detail, geb] = await Promise.all([
          getInrichtingHistorie(id),
          getInrichtingDetail(id),
          getGebieden(undefined, true),
        ]);
        if (!actief) return;
        setSlices(hist.data || []);
        setHouder(detail.data?.houder || null);
        setGebieden(geb.data || []);
        setFout(null);
      } catch {
        if (actief) setFout('Kon de historie niet laden.');
      } finally {
        if (actief) setLaden(false);
      }
    })();
    return () => { actief = false; };
  }, [id]);

  /* Alle perioden, oudste eerst — ook het lopende recht. Dat is er één van;
     dat het nog niet is afgesloten maakt voor deze weergave niets uit, en het
     scheelt dat je vanuit het lopende recht gewoon terug kunt bladeren. */
  const perioden = [...slices]
    .sort((a, b) => (a.inwerkingtreding || '').localeCompare(b.inwerkingtreding || ''));
  const idx = perioden.findIndex(s => s.periode_id === periode_id);
  const slice = idx >= 0 ? perioden[idx] : null;
  const ouder = idx > 0 ? perioden[idx - 1] : null;       // ‹ ouder
  const nieuwer = idx >= 0 && idx < perioden.length - 1 ? perioden[idx + 1] : null;  // › nieuwer

  const gaNaar = (s) => s && navigate(`/beregening/inrichtingen/${id}/historie/${s.periode_id}`);

  const isVergunning = slice?.rechtstype === 'vergunning';
  const putten = slice?.putten || [];

  return (
    <Modal isOpen={true} onClose={sluit} maxWidth={900}>
      {laden ? (
        <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
      ) : fout ? (
        <div className="alert alert-error">{fout}</div>
      ) : !slice ? (
        <div className="alert alert-error">Dit gebruiksrecht is niet gevonden.</div>
      ) : (
        <div>
          {/* Header met stepper rechtsboven */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22 }}>
            <div>
              <div className="historie-eyebrow">
                {slice.is_huidig ? 'Lopend gebruiksrecht' : 'Historische weergave'}
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 4px' }}>
                {fmtId(id)}
              </h2>
              <div style={{ fontSize: 15, color: 'var(--text)', marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>{kenmerkTypeLabel(slice.kenmerk_type)} </span>
                <span className="mono">{slice.kenmerk}</span>
              </div>
              <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>
                {fmtDatumNL(slice.inwerkingtreding)} t/m {slice.uitwerkingtreding ? fmtDatumNL(slice.uitwerkingtreding) : 'heden'}
              </div>
            </div>
            {perioden.length > 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, flexShrink: 0 }}>
                {ouder && (
                  <button className="historie-step" onClick={() => gaNaar(ouder)}>
                    <span className="chevron">‹</span> recht vanaf {fmtDatumNL(ouder.inwerkingtreding)}
                  </button>
                )}
                {nieuwer && (
                  <button className="historie-step" onClick={() => gaNaar(nieuwer)}>
                    <span className="chevron">›</span> recht vanaf {fmtDatumNL(nieuwer.inwerkingtreding)}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Putten + kaart */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <h3 className="card-title">
                Putten <span className="chip" style={{ marginLeft: 8 }}>{putten.length}</span>
              </h3>
            </div>
            {putten.length > 0 ? (
              <>
                <KaartMetLegenda
                  punten={putten.map(p => ({
                    put_id: p.put_id,
                    x_rd: p.x_rd,
                    y_rd: p.y_rd,
                    perceel: p.perceel,
                    onClick: () => navigate(`/beregening/putten/${p.put_id}`),
                  }))}
                  overlays={gebieden}
                  hoogte={300}
                />
                <div style={{ marginTop: 16 }} />
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Coördinaten (RD)</th>
                      <th>Opgegeven diepte</th>
                      <th>Filterdiepte (bovenkant)</th>
                      <th>Perceel</th>
                      <th>Periode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {putten.map(p => (
                      <tr
                        key={p.put_id}
                        className="clickable"
                        onClick={() => navigate(`/beregening/putten/${p.put_id}`)}
                      >
                        <td><span className="mono">{fmtId(p.put_id)}</span></td>
                        <td>{Math.round(p.x_rd)}, {Math.round(p.y_rd)}</td>
                        <td>{p.opgegeven_diepte_m ?? '—'}</td>
                        <td>{p.diepte_bv_filter_m ?? '—'}</td>
                        <td>{p.perceel || '—'}</td>
                        <td style={{ fontSize: 13, color: 'var(--text-soft)' }}>
                          {fmtDatumNL(p.begin_datum_koppeling)} → {p.eind_datum_koppeling ? fmtDatumNL(p.eind_datum_koppeling) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Geen putten in dit recht.</p>
            )}
          </div>

          {/* Recht */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <h3 className="card-title">{isVergunning ? 'Vergunning' : 'Melding'}</h3>
            </div>
            <div className="field-grid cols-3" style={{ gap: 12 }}>
              <Veld label={kenmerkTypeLabel(slice.kenmerk_type)}>{slice.kenmerk || '—'}</Veld>
              <Veld label="Rechtstype">{slice.rechtstype || '—'}</Veld>
              <Veld label="Doelen">
                {slice.doelen && slice.doelen.length > 0 ? slice.doelen.map(doelLabel).join(', ') : '—'}
              </Veld>
              {/* Een vergunning wordt niet gemeld maar verleend; die regel zou
                  daar altijd leeg staan. */}
              {!isVergunning && (
                <Veld label="Meldingsdatum">{fmtDatumNL(slice.meldingsdatum)}</Veld>
              )}
              <Veld label="Inwerkingtreding">{fmtDatumNL(slice.inwerkingtreding)}</Veld>
              <Veld label="Uitwerkingtreding">{slice.uitwerkingtreding ? fmtDatumNL(slice.uitwerkingtreding) : '—'}</Veld>
              {isVergunning ? (
                <>
                  <Veld label="Vergund uurdebiet (m³)">{slice.vergund_debiet_uur ?? '—'}</Veld>
                  <Veld label="Vergund maanddebiet (m³)">{slice.vergund_debiet_maand ?? '—'}</Veld>
                  <Veld label="Vergund kwartaaldebiet (m³)">{slice.vergund_debiet_kwartaal ?? '—'}</Veld>
                  <Veld label="Vergund jaardebiet (m³)">{slice.vergund_debiet_jaar ?? '—'}</Veld>
                  {slice.wijzigingsbesluitnummer && (
                    <Veld label="Wijzigingsbesluit">{slice.wijzigingsbesluitnummer}</Veld>
                  )}
                </>
              ) : (
                <Veld label="Max pompcapaciteit (m³/u)">{slice.max_pompcap_m3_uur ?? '—'}</Veld>
              )}
              <Veld label="Bedrijfsomvang (ha)">{slice.bedrijfsomvang_ha ?? '—'}</Veld>
            </div>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)' }}>
              {slice.reden && <div>Reden archivering: {slice.reden}</div>}
              <div>
                Gewijzigd door: {slice.gewijzigd_door || 'onbekend'}
                {slice.gearchiveerd_op && <> · gearchiveerd {fmtDatumNL(slice.gearchiveerd_op)}</>}
              </div>
            </div>
          </div>

          {/* Houder */}
          {houder && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">{isVergunning ? 'Vergunninghouder' : 'Melder'}</h3>
              </div>
              <div className="field-grid cols-3" style={{ gap: 12 }}>
                <Veld label="Naam">{houder.naam || '—'}</Veld>
                <Veld label="Type">{houder.type === 'RP' ? 'Rechtspersoon' : 'Natuurlijk persoon'}</Veld>
                <Veld label="Plaats">{houder.plaats || '—'}</Veld>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
